import React, { useEffect } from 'react';
import { useReactTable, createColumnHelper, getCoreRowModel, getSortedRowModel, flexRender } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useGetForTable } from '../../hooks/statistic/Feedback';
import { useNavigate } from 'react-router-dom';
import { useGetVendorId } from '../../hooks/global/User';
import * as XLSX from 'xlsx';
import { selectTextByLanguage, useGetAvailableTranslation } from '../../hooks/global/Languages';
import { Row, Col } from "react-bootstrap";

function ReportTableV8({ module, baseDate, compareDate }) {
    const { t, i18n } = useTranslation();
    const lang = i18n.language;
    const columnHelper = createColumnHelper();
    const vendorId = useGetVendorId();

    const availableLanguages = useGetAvailableTranslation();

    const from = baseDate?.startDate?.valueOf();
    const to = baseDate?.endDate?.valueOf();
    const compareFrom = compareDate?.startDate?.valueOf();
    const compareTo = compareDate?.endDate?.valueOf();

    const moduleId = module?.company ? "COMPANY" : module?.id || "ALL";

    const { data: base, isLoading: baseisLoading, isError: baseIsError, refetch: baseRefetch, isFetching: baseIsFetching } = useGetForTable(moduleId, "", from, to, "table");
    const { data: compare, isLoading: compareIsLoading, isError: compareIsError, refetch: compareRefetch, isFetching: compareIsFetching } = useGetForTable(moduleId, "", compareFrom, compareTo, "table2");

    const navigate = useNavigate();

    const [sorting, setSorting] = React.useState([]);
    const [columns, setColumns] = React.useState([]);
    const [basicData, setBaseData] = React.useState([]);

    useEffect(() => {
        if (module) { baseRefetch() };
        if (module && !!compareFrom && !!compareTo) compareRefetch();

    }, [module, baseDate, compareDate, baseRefetch, compareRefetch, from, to, compareFrom, compareTo, lang]);

    const RatingDifferenceCell = (value) => {
        // Assuming 'value' is the difference in ratings
        const val = value?.getValue();

        const v1 = Number(val?.v1) || null;
        const v2 = Number(val?.v2) || null;

        const difference = (v1 - v2).toFixed(1) || 0;
        const arrow = difference > 0 ? " ▲ " : difference < 0 ? " ▼ " : " ● "; // Add circle for zero
        const color = difference > 0 ? "#5fd65f" : difference < 0 ? "#ff023c" : "#ffc402"; // Choose a color for the circle

        return (
            <div>
                {v1?.toFixed(1)}
                <span
                    style={{
                        lineHeight: "12px",
                        color: color,
                        fontSize: difference === 0 ? "large" : "small",
                    }}>
                    {v2 && arrow}
                </span>
                {v2 && <span style={{ opacity: ".5" }}> {"(" + (difference > 0 ? "+" : "") + difference + ")"} </span>}
            </div>
        );
    };

    const sortFunction = (rowA, rowB, id, desc) => {

        const valueA = rowA.original[id]?.v1 || 0;
        const valueB = rowB.original[id]?.v1 || 0;
        
        return Number(valueA) - Number(valueB);
    }

    const PercentageDifferenceCell = (value) => {
        const val = value?.getValue() ?? {};

        const v1 = Number(val?.v1) || null;
        const v2 = Number(val?.v2) || null;

        const difference = (v1 - v2).toFixed(1) || 0;

        const arrow = difference > 0 ? "▲" : difference < 0 ? "▼" : "●";
        const color = difference > 0 ? "#5fd65f" : difference < 0 ? "#ff023c" : "#ffc402";

        return (
            <div>
                {v1?.toFixed(1)}{v1 && <>%</>}
                <span style={{
                    lineHeight: "12px",
                    color: color,
                    fontSize: difference === 0 ? "large" : "small",
                }}>
                    {v2 && arrow}
                </span>
                {v2 && <span style={{ opacity: ".5" }}> {"(" + (difference > 0 ? "+" : "") + difference + "%)"} </span>}
            </div>
        );
    };

    const getTextWidth = (text, font) => {
        // Create a canvas element which is used for measuring text
        const canvas = getTextWidth.canvas || (getTextWidth.canvas = document.createElement("canvas"));
        const context = canvas.getContext("2d");
        context.font = font;
        const metrics = context.measureText(text);
        return (metrics.width / 2) + 60;
    };

    const prepareColumnsFromQuestions = (data, compare) => {
        const questionColumns = new Map();
        const questionData = new Map();

        data.forEach((company) => {
            company.questionsStatistics.forEach((question) => {
                const headerText = selectTextByLanguage(question?.text, lang, availableLanguages);  //question?.text[lang] || "NO TEXT FOUND";
                //const size = headerText?.length * 8 || 50;
                const size = getTextWidth(headerText, "12px Open Sans");
                const numOfAnswers = question?.answer.length;

                let group = {};
                if (!questionColumns.has(question.questionId)) {
                    group = { id: question.questionId, header: headerText, columns: [] };//{ id: question.questionId, header: headerText, columns: [] };
                } else {
                    group = questionColumns.get(question.questionId);
                }
                if (numOfAnswers === 0) {
                    const column = columnHelper.accessor(question.questionId, {
                        header: () => headerText, // Use the question text as header
                        cell: RatingDifferenceCell,
                        sortingFn: sortFunction,
                        sortUndefined: -1,
                        type: question?.type,
                        size: size,
                    });
                    group.header = "";
                    group.size = size;
                    const columnIndex = group.columns.findIndex(
                        (c) => c.accessorKey === question.questionId
                    );
                    if (columnIndex === -1) group.columns.push(column);
                    else group.columns[columnIndex] = column;
                } else {
                    //group.header = "1";
                    question?.answer.forEach((answer) => {
                        let answerText = answer && answer.text && selectTextByLanguage(answer.text, lang, availableLanguages);
                        let answerValue = answer.value ? answer.value.replace(/[^a-zA-Z0-9 ]/g, '') : answerText.replace(/[^a-zA-Z0-9 ]/g, '');

                        if (question.type === "CHECKBOX")
                            answerText = t(`admin.common.${answer.value}`);


                        const column = {
                            header: () => answerText,
                            cell: PercentageDifferenceCell,
                            sortingFn: sortFunction,
                            sortUndefined: -1,
                            type: question?.type,
                            proc: true,
                            size: size,
                        };
                        const columnIndex = group.columns.findIndex(
                            (c) => c.accessorKey === question.questionId + "-" + answerValue
                        );

                        if (columnIndex === -1)
                            group.columns.push(columnHelper.accessor(question.questionId + "-" + answerValue, column));
                        else
                            group.columns[columnIndex] = columnHelper.accessor(question.questionId + "-" + answerValue, column);
                    });
                }
                questionColumns.set(question.questionId, columnHelper.group(group));
            });
        });

        data.forEach((company) => {
            const company2 = compare?.find((q) => q.id === company.id);

            let data = {
                id: company.id,
                name: { name: company.name, id: company.id },
                avgRating: {
                    v1: company?.avgRating.toFixed(1),
                    v2: company2?.avgRating.toFixed(1),
                },
                count: { v1: company.count.toFixed(1), v2: company2?.count.toFixed(1) },
            };
            questionColumns.forEach((column, questionId) => {
                const question = company.questionsStatistics.find(
                    (q) => q.questionId === questionId
                );
                const question2 = company2?.questionsStatistics.find(
                    (q) => q.questionId === questionId
                );

                if (question?.answer.length === 0) {

                    let val, val2 = 0;
                    if (question.type == "TEXTAREA") {
                        val = question?.rating?.count?.toFixed(1);
                        val2 = question2?.rating?.count?.toFixed(1);
                    }
                    else {
                        val = question?.rating?.avgRating?.toFixed(1);
                        val2 = question2?.rating?.avgRating?.toFixed(1);
                    }

                    data[questionId] = { v1: val, count: question?.rating?.count, v2: val2 };
                } else {

                    let totalCount = question?.answer.reduce((sum, answer) => sum + answer?.count, 0);
                    let totalCount2 = question2?.answer.reduce((sum, answer) => sum + answer?.count, 0);

                    question?.answer.forEach((answer, i) => {

                        let answerText = answer && answer.text && selectTextByLanguage(answer.text, lang, availableLanguages);
                        let answerValue = answer.value ? answer.value.replace(/[^a-zA-Z0-9 ]/g, '') : answerText.replace(/[^a-zA-Z0-9 ]/g, '');

                        let percentage = (answer.count / totalCount) * 100;
                        let val = percentage.toFixed(1);
                        let val2 = (question2?.answer[i]?.count / totalCount2) * 100;

                        data[question.questionId + "-" + answerValue] = { v1: val, count: question?.rating?.count, v2: val2 };
                    });
                }
            });
            questionData.set(company.name, data);
        });


        const handleClik = (e) => {
            let url = `/admin/vendors/${vendorId}/reviews?selectedModuleId=`;
            url += moduleId === "COMPANY" ? "COMPANY" : moduleId;
            url += `&selectedElementId=${e}`;
            url += `&createdAfter=${from}`;
            url += `&createdBefore=${to}`;
            navigate(url);
        }

        return {
            data: Array.from(questionData.values()), column: [
                columnHelper.accessor('name', {
                    header: () => '',
                    cell: (info) => {
                        const value = info?.getValue();
                        return <a href='#' onClick={() => handleClik(value.id)} >{selectTextByLanguage(value.name, lang, availableLanguages) /*typeof value.name === 'object' ? "value.name[lang]" : "value.name"*/}</a> 
                    },
                    type: 'FIXED',
                }),
                columnHelper.accessor('count', {
                    header: () => t('common.revNum'),
                    cell: RatingDifferenceCell,
                    sortingFn: sortFunction,
                    sortUndefined: -1,
                    type: 'FIXED',
                }),
                columnHelper.accessor('avgRating', {
                    header: () => t('common.revAvg'),
                    cell: RatingDifferenceCell,
                    sortingFn: sortFunction,
                    sortUndefined: -1,
                    type: 'FIXED',
                }),
                ...Array.from(questionColumns.values()),
            ]
        };
    };
    useEffect(() => {
        if (base) {
            const dynamicColumns = prepareColumnsFromQuestions(base, compareFrom ? compare : null);
            setColumns(dynamicColumns.column);
            setBaseData(dynamicColumns.data)

        }
    }, [base, compare, compareFrom, lang]);


    const table = useReactTable({
        data: basicData,
        columns: columns,
        defaultColumn: {
            minSize: 150,
            maxSize: 800,
        },
        state: { sorting },
        columnResizeMode: 'onChange',
        columnResizeDirection: 'ltr',
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        debugTable: false,
    });


    const prepareDataForExport = (table, selector) => {
        let merges = [];
        let colmCount = 0;
        const headers = table.getHeaderGroups().map((headerGroup, j) => {
           return headerGroup.headers.map((header, i) => {
                let headerResult = "";
                if(header.subHeaders.length > 0) {
                    if(header.subHeaders.length>1) {
                        merges.push({ s: { r: j, c: colmCount }, e: { r: j, c: colmCount + (header.subHeaders.length - 1) } });
                    }
                    headerResult =  header.subHeaders.map((subHeader, x) => {
                        const headerContent = j === 0 && i < 3 ? '' : typeof header.column.columnDef.header === 'function' ? header.column.columnDef.header() : header.column.columnDef.header;
                        const textValue = headerContent.toString().replace(/"/g, '""');
                        colmCount++;
                        return { v: textValue, t: 's' };
                    });
                }
                else {
                    const headerContent = j === 0 && i < 3 ? '' : typeof header.column.columnDef.header === 'function' ? header.column.columnDef.header() : header.column.columnDef.header;
                    const textValue = headerContent.toString().replace(/"/g, '""');
                    headerResult =  { v: textValue, t: 's' };
                    colmCount++;
                }
                return headerResult;
            }).flat();
        });
    
        const data = table.getRowModel().rows.map(row =>
            row.getVisibleCells().map((cell, i) => {

                const cellContent = i === 0 ? selectTextByLanguage(cell?.getValue()?.name, lang) : cell?.getValue()?.[selector];
                let textValue = (cellContent) ? cellContent : "";
                let z = '';
                if(cell.column.columnDef.proc && textValue) {
                    textValue = textValue /100;
                    z = '0.00%';
                }

                return { v: textValue, t: 'n', z: z};
            })
        );

        return { headers, data, merges };
    }


    const exportTableToExcel = (table, filename = 'table-export') => {

        const { headers, data, merges } = prepareDataForExport(table,"v1");
        const worksheetData = [...headers, ...data];
        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
        worksheet['!merges'] = merges;

        const { headers:headers2, data:data2, merges:merges2 } = prepareDataForExport(table,"v2");
        const worksheetData2 = [...headers2, ...data2];
        const worksheet2 = XLSX.utils.aoa_to_sheet(worksheetData2);
        worksheet2['!merges'] = merges2;

           // Create a worksheet for the difference
    const maxLength = Math.max(worksheetData.length, worksheetData2.length);
    const maxWidth = Math.max(headers[0].length, headers2[0].length);

    const diffData = Array.from({ length: maxLength }, (_, rowIndex) => (
        Array.from({ length: maxWidth }, (_, colIndex) => {
            const cell1 = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
            const cell2 = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
            if (rowIndex < headers.length && colIndex < headers[0].length) {
                return headers[rowIndex][colIndex];
            } else {
                return {
                    f: `IF(AND(ISNUMBER(Base!${cell1}), ISNUMBER(Comparison!${cell2})), Base!${cell1} - Comparison!${cell2}, Base!${cell1})`
                };
            }
        })
    ));
    const worksheet3 = XLSX.utils.aoa_to_sheet(diffData);
    
        // Create a workbook and add the worksheet
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Base');
        XLSX.utils.book_append_sheet(workbook, worksheet2, 'Comparison'); 
        XLSX.utils.book_append_sheet(workbook, worksheet3, 'Difference');
    
        // Write the workbook to a file
        XLSX.writeFile(workbook, `${filename} - ${t('admin.report.h1')}.xlsx`);
    };
    




    if (baseisLoading || baseIsFetching) return <></>
    if (base.length === 0) return (<div className="container noReportRate pt-4" style={{ fontSize: "14px" }}>{t('admin.common.noRevCond')}</div>);

    

    return (

    
        <Row className="adminReportDataTable">
            <Col md={12}>
                <div className="dataTableSection">
                    <div className="dataTableSectionHeader">
                        <p className="text-right"><a className="dataTableExport" onClick={(e) => { e.preventDefault(); exportTableToExcel(table, module.name) }} href="#">{t("admin.common.CSV")}</a></p>
                    </div>
                    <div className="dataTableWrap">
                        <div className='ReactTable ' style={{ overflow: 'auto', position: 'relative', maxHeight: '800px', width: '100%', padding: '0px 0px 0px 0px' }}>
                            <table className='table table-striped table-bordered' style={{ width: table.getTotalSize(), minWidth: '100%' }} >
                                <thead className='sticky-header' >
                                    {table.getHeaderGroups().map((headerGroup) => (
                                        <tr className='rt-tr' key={headerGroup.id}>
                                            {headerGroup.headers.map((header, i) => (
                                                <th key={header.id} className={`-sort-${header.column.getIsSorted()} rt-th cursor-pointer ${i === 0 ? 'sticky-column z-3' : ''}`} colSpan={header.colSpan} style={{ width: header.getSize() }}>

                                                    {header.isPlaceholder ? <div className='rt-td-empty-content'></div> : (<div className='rt-td-content'>
                                                        <div className='rt-resizable-header-content'>
                                                            <div className={'cursor-pointer select-none finalTitle'} >
                                                                <div onClick={header.column.getToggleSortingHandler()} >
                                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                                    <small>{header.column.getCanSort() ? ({ asc: ' ▲', desc: '  ▼' }[header.column.getIsSorted()] ?? null) : null}</small>
                                                                </div>
                                                                {(header.column.columnDef.type && header.column.columnDef.type !== 'FIXED') && <div className="finalTag"><span>{t(`common.${header.column.columnDef.type}`)}</span></div>}
                                                            </div>
                                                        </div>
                                                        <div onDoubleClick={() => header.column.resetSize()} onMouseDown={header.getResizeHandler()} onTouchStart={header.getResizeHandler()} className={`resizer ${table.options.columnResizeDirection} ${header.column.getIsResizing() ? 'isResizing' : ''}`} style={{ transform: header.column.getIsResizing() ? 'none' : '' }}></div>
                                                    </div>
                                                    )}
                                                </th>
                                            ))}
                                        </tr>
                                    ))}
                                </thead>
                                <tbody className=''>
                                    {table.getRowModel().rows.map(row => (
                                        <tr className='rt-tr' key={row.id}>
                                            {row.getVisibleCells().map((cell, i) => (
                                                <td className={`${i === 0 ? 'rt-td sticky-column finalTitle' : 'rt-td'}`} key={cell.id}>
                                                    <div className='rt-td-content'>{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
                                                </td>
                                            ))}

                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="dataTableSectionHeader">
                        <p className="inline dataTableSectionIndex">
                            <span className="zero">{t("admin.report.sRate")}</span>
                            <span className="positive">{t("admin.report.bRate")}</span>
                            <span className="negative">{t("admin.report.wRate")}</span>
                        </p>
                    </div>
                </div>
            </Col>
        </Row>
    );
}

export default ReportTableV8;
